/**
 * TableOptions sheet (spec §2.4): every toggle bound to `OptionsState` with House Rules defaults,
 * Blitz preset, format quick / classic 500 / classic 300. The last-used options persist in
 * localStorage `sn.tableOptions` so reopening the sheet keeps them.
 */
import { useEffect, useRef, useState } from 'react';
import { safeStorage } from '../../store/store';
import { type FormatChoice, OPTION_DEFS, type OptionsState, defaultOptions, summarize } from '../tableOptions';
import { Button, Segmented, Sheet, Toggle } from '../ui';

export const OPTIONS_STORAGE_KEY = 'sn.tableOptions';
const storage = safeStorage();

export function loadSavedOptions(): OptionsState {
  try {
    const raw = storage.getItem(OPTIONS_STORAGE_KEY);
    if (!raw) return defaultOptions();
    return { ...defaultOptions(), ...(JSON.parse(raw) as Partial<OptionsState>) };
  } catch {
    return defaultOptions();
  }
}

export function saveOptions(s: OptionsState): void {
  storage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(s));
}

const FORMATS: ReadonlyArray<{ value: FormatChoice; label: string }> = [
  { value: 'quick', label: '4 hands' },
  { value: 'classic500', label: 'To 500' },
  { value: 'classic300', label: 'To 300' },
];

export interface TableOptionsSheetProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (s: OptionsState) => void;
  initial?: OptionsState;
  submitLabel?: string;
}

export function TableOptionsSheet({ open, onClose, onSubmit, initial, submitLabel = 'Create table' }: TableOptionsSheetProps) {
  const [state, setState] = useState<OptionsState>(() => initial ?? loadSavedOptions());

  // Seed on the *open transition* only.
  //
  // `initial` is a fresh object every render at the call site (Lobby derives it from
  // `table.rulesJson`), so depending on it re-seeded the sheet on every parent render — and with
  // the host's auto-start countdown ticking four times a second, a toggle the player flipped
  // snapped back within 250ms and Save sent the unchanged set. `initialRef` keeps the latest value
  // available without making it a trigger.
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) setState(initialRef.current ?? loadSavedOptions());
    wasOpen.current = open;
  }, [open]);

  const set = <K extends keyof OptionsState>(key: K, value: OptionsState[K]) => {
    setState((s) => {
      const next = { ...s, [key]: value };
      saveOptions(next);
      return next;
    });
  };

  return (
    <Sheet open={open} title="Table rules" onClose={onClose} testId="table-options">
      <div className="stack">
        <div className="row row--between row--stack-sm">
          <div className="eyebrow">Match</div>
          <Segmented value={state.format} options={FORMATS} onChange={(v) => set('format', v)} label="Match format" testId="format" />
        </div>
        {OPTION_DEFS.map((def) => (
          <Toggle
            key={def.key}
            label={def.label}
            hint={def.hint}
            checked={state[def.key] as boolean}
            onChange={(v) => set(def.key, v as never)}
            testId={`opt-${def.key}`}
          />
        ))}
        <div className="small muted" data-testid="options-summary">
          {summarize(state)}
        </div>
        <div className="row">
          <Button
            variant="ghost"
            testId="options-reset"
            onClick={() => {
              const d = defaultOptions();
              saveOptions(d);
              setState(d);
            }}
          >
            House Rules
          </Button>
          <Button variant="primary" className="grow" testId="options-submit" onClick={() => onSubmit(state)}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
